import DataList from "components/datatable/DataList";
import React, { useMemo } from "react";
import { useAuth } from "context/AuthProvider";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import { RuleRequired } from "utils/MyUtils";
import {
  boolText,
  hasPermission,
  parseBoolean,
  parseGroupIDs,
  showEmpty,
} from "./SystemPageUtils";

export default function MenuManagementPage() {
  const { user } = useAuth();
  const [menusResp] = useAxios({
    method: "GET",
    url: "/system/admin/menus",
    params: { perpage: 500 },
    auth: true,
  });
  const [groupsResp] = useAxios({
    method: "GET",
    url: "/system/groups",
    params: { perpage: 500 },
    auth: true,
  });
  const [, , , axiosFetch] = useAxiosFunction();
  const canManage = hasPermission(user, "system.menu.manage");

  const menus = Array.isArray(menusResp?.data) ? menusResp.data : [];
  const groups = Array.isArray(groupsResp?.data) ? groupsResp.data : [];
  const columns = canManage
    ? [
        "no",
        "menu_code",
        "menu_name",
        "parent_name",
        "route",
        "assigned_groups",
        "sort_order",
        "is_visible",
        "is_active",
        "action",
      ]
    : [
        "no",
        "menu_code",
        "menu_name",
        "parent_name",
        "route",
        "assigned_groups",
        "sort_order",
        "is_visible",
        "is_active",
      ];
  const parentOptions = useMemo(
    () => [{ id_menu: "", menu_name: "Tanpa Parent" }, ...menus],
    [menus],
  );

  const coltype = useMemo(
    () => ({
      id_menu: { type: "hidden", name: "id_menu" },
      id_parent: {
        label: "Parent Menu",
        type: "dropdown",
        name: "id_parent",
        colsize: 4,
        options: parentOptions,
        valueId: "id_menu",
        labelId: "menu_name",
        placeholder: "-Tanpa Parent-",
      },
      menu_code: {
        label: "Menu Code",
        type: "text",
        name: "menu_code",
        colsize: 3,
        validation: RuleRequired,
        width: 150,
      },
      menu_name: {
        label: "Menu Name",
        type: "text",
        name: "menu_name",
        colsize: 5,
        validation: RuleRequired,
        width: 220,
      },
      route: {
        label: "Route",
        type: "text",
        name: "route",
        colsize: 4,
        renderFunc: (val) => showEmpty(val),
        width: 180,
      },
      icon: {
        label: "Icon",
        type: "text",
        name: "icon",
        colsize: 3,
        renderFunc: (val) => showEmpty(val),
        width: 140,
      },
      sort_order: {
        label: "Sort Order",
        type: "text",
        name: "sort_order",
        colsize: 2,
        renderFunc: (val) => showEmpty(val, "0"),
        width: 90,
        align: "center",
      },
      is_visible: {
        label: "Visible",
        type: "checkbox",
        name: "is_visible",
        colsize: 2,
        renderFunc: (val) => (parseBoolean(val) ? "YA" : "TIDAK"),
        width: 90,
        align: "center",
      },
      is_active: {
        label: "Status",
        type: "checkbox",
        name: "is_active",
        colsize: 2,
        renderFunc: (val) => boolText(val),
        width: 100,
        align: "center",
      },
      group_ids: {
        label: "Visible untuk Group",
        type: "listcheckbox",
        name: "group_ids",
        colsize: 12,
        checklist: groups,
        valueId: "id_group",
        labelId: "groupname",
        itemcolsize: 4,
      },
      parent_name: { label: "Parent", renderFunc: (val) => showEmpty(val) },
      assigned_groups: { label: "Group", renderFunc: (val) => showEmpty(val) },
      action: { label: "Aksi", align: "center", width: 100 },
    }),
    [groups, parentOptions],
  );

  const saveMenu = async (form, defaultValue = {}) => {
    const id = form?.id_menu || defaultValue?.id_menu || "";
    const method = id ? "PUT" : "POST";
    const url = id ? `/system/menus/${id}` : "/system/menus";

    return axiosFetch({
      method,
      url,
      auth: true,
      data: {
        id_parent: Number(form.id_parent || 0),
        menu_code: form.menu_code,
        menu_name: form.menu_name,
        route: form.route || "",
        icon: form.icon || "",
        sort_order: Number(form.sort_order || 0),
        is_visible: Boolean(form.is_visible),
        is_active: Boolean(form.is_active),
        group_ids: parseGroupIDs(form.group_ids),
      },
    });
  };

  return (
    <>
      {!canManage ? (
        <div className="alert alert-info">
          Mode baca saja. User aktif tidak memiliki permission
          `system.menu.manage`.
        </div>
      ) : null}
      <DataList
        title="Daftar Menu"
        coltype={coltype}
        dlist={{
          url: "/system/admin/menus",
          columns,
          filter: [{ dtype: "q", name: "q", colsize: 4 }],
          sort_by: ["menu_code", "menu_name", "parent_name", "route"],
        }}
        addconfig={
          canManage
            ? {
                columns: [
                  "id_parent",
                  "menu_code",
                  "menu_name",
                  "route",
                  "icon",
                  "sort_order",
                  "is_visible",
                  "is_active",
                  "group_ids",
                ],
                title: "Penambahan Menu",
                label_tool: "Tambah Menu",
                saveHandle: saveMenu,
              }
            : null
        }
        editconfig={
          canManage
            ? {
                columns: [
                  "id_menu",
                  "id_parent",
                  "menu_code",
                  "menu_name",
                  "route",
                  "icon",
                  "sort_order",
                  "is_visible",
                  "is_active",
                  "group_ids",
                ],
                title: "Perubahan Menu",
                url: "/system/menus",
                urlitem: "/system/menus",
                cellid: "id_menu",
                saveHandle: saveMenu,
                normalizeItem: (item) => ({
                  ...item,
                  id_parent: item?.id_parent ? String(item.id_parent) : "",
                  sort_order: item?.sort_order ?? 0,
                  is_visible: parseBoolean(item?.is_visible),
                  is_active: parseBoolean(item?.is_active),
                  group_ids: parseGroupIDs(
                    item?.group_ids || item?.assigned_group_ids,
                  ),
                }),
              }
            : null
        }
        delconfig={
          canManage
            ? {
                url: "/system/menus",
                cellid: "id_menu",
                cellname: "menu_name",
              }
            : null
        }
        activateconfig={
          canManage
            ? {
                url: "/system/menus",
                cellid: "id_menu",
                cellname: "menu_name",
                cellactivate: "is_active",
                labelactivate: "Aktifkan",
                labelinactivate: "Non-aktifkan",
                cfmactivate: "mengaktifkan",
                cfminactivate: "menonaktifkan",
                infoactivate: "diaktifkan",
                infoinactivate: "dinonaktifkan",
              }
            : null
        }
      />
    </>
  );
}
